// PosterTemplateEngine.js — Professional Social Media Poster Templates
// Architecture: AI generates background photo ONLY → this engine applies ALL design elements
//   (exact brand hex colours, Google Fonts, logo, layout, typography hierarchy, CTA buttons)
//
// Exports:
//   TEMPLATES          – array of template metadata
//   renderTemplate     – async (templateId, options, overrideDims?) → data URL
//   renderThumbnail    – async (templateId, brandColours, fontStyle, size?) → data URL
//   ensureFontsLoaded  – async () → preloads all Google Fonts

// ─── Template Metadata ────────────────────────────────────────────────────────

export const TEMPLATES = [
  {
    id: 'bottom_band',
    name: 'Bottom Band',
    desc: 'Photo fills canvas · brand gradient band anchors the bottom',
    icon: 'fas fa-layer-group',
    textPosition: 'bottom',
  },
  {
    id: 'split_panel',
    name: 'Split Panel',
    desc: 'Solid brand colour panel left · photo right',
    icon: 'fas fa-columns',
    textPosition: 'center',
  },
  {
    id: 'frosted_card',
    name: 'Frosted Card',
    desc: 'Full photo · floating frosted glass card at bottom',
    icon: 'fas fa-square',
    textPosition: 'bottom',
  },
  {
    id: 'bold_overlay',
    name: 'Bold Overlay',
    desc: 'Vivid brand overlay over photo · centred impactful text',
    icon: 'fas fa-fill-drip',
    textPosition: 'center',
  },
  {
    id: 'top_bar',
    name: 'Brand Bar',
    desc: 'Solid brand header · photo · dark footer with text',
    icon: 'fas fa-grip-lines',
    textPosition: 'bottom',
  },
  {
    id: 'editorial',
    name: 'Editorial',
    desc: 'Photo hero top · clean white text panel below',
    icon: 'fas fa-newspaper',
    textPosition: 'bottom',
  },
];

// ─── Canvas Dimensions ────────────────────────────────────────────────────────

const CANVAS_DIMS = {
  square:    [1080, 1080],
  portrait:  [1080, 1350],
  story:     [1080, 1920],
  landscape: [1920, 1080],
};

// ─── Font Map ─────────────────────────────────────────────────────────────────

const FONT_MAP = {
  modern_clean:     'Poppins',
  bold_strong:      'Montserrat',
  elegant_serif:    'Playfair Display',
  friendly_rounded: 'Nunito',
};
const DEFAULT_FONT = 'Inter';

function getFont(fontStyle) {
  return FONT_MAP[fontStyle] || DEFAULT_FONT;
}

// ─── Colour Utilities ─────────────────────────────────────────────────────────

function hexToRgb(hex) {
  const h   = (hex || '#000000').replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const int  = parseInt(full, 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

function rgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

function luminance({ r, g, b }) {
  const lin = c => { const n = c / 255; return n <= 0.04045 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4); };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function isLight(hex) {
  return luminance(hexToRgb(hex || '#000000')) > 0.35;
}

// Best contrasting text colour (white or near-black) for a given background hex
function textOn(hex) {
  return isLight(hex) ? '#111827' : '#FFFFFF';
}

// ─── Font Loading (Google Fonts via FontFace API) ─────────────────────────────

let _fontsLoaded  = false;
let _fontsPromise = null;

export async function ensureFontsLoaded() {
  if (_fontsLoaded) return;
  if (_fontsPromise) return _fontsPromise;

  _fontsPromise = (async () => {
    const LINK_ID = 'dte-poster-fonts';
    if (!document.getElementById(LINK_ID)) {
      const link  = document.createElement('link');
      link.id     = LINK_ID;
      link.rel    = 'stylesheet';
      link.href   = 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700;800;900&family=Montserrat:wght@400;600;700;800;900&family=Playfair+Display:wght@400;700;900&family=Nunito:wght@400;600;700;800&family=Inter:wght@400;500;600;700&display=swap';
      document.head.appendChild(link);
    }
    // Wait for at least the primary variants — 4 s timeout
    const loads = [
      document.fonts.load('700 48px Poppins'),
      document.fonts.load('700 48px Montserrat'),
      document.fonts.load('700 48px "Playfair Display"'),
      document.fonts.load('700 48px Nunito'),
      document.fonts.load('600 48px Inter'),
    ];
    await Promise.race([Promise.all(loads), new Promise(r => setTimeout(r, 4000))]);
    _fontsLoaded = true;
  })();

  return _fontsPromise;
}

// ─── Canvas Drawing Helpers ───────────────────────────────────────────────────

// Wrap text to lines that fit within maxWidth
function wrapText(ctx, text, maxWidth) {
  if (!text) return [];
  const words = text.split(' ');
  const lines = [];
  let cur = '';
  for (const w of words) {
    const test = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && cur) { lines.push(cur); cur = w; }
    else cur = test;
  }
  if (cur) lines.push(cur);
  return lines;
}

// Draw a rounded rectangle path (call fill/stroke after)
function roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);   ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r); ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);   ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);       ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// Draw image as cover-fill inside a clipped rectangle
function drawCoverImage(ctx, img, x, y, w, h) {
  if (!img) {
    // Grey placeholder if no image
    ctx.fillStyle = '#94A3B8';
    ctx.fillRect(x, y, w, h);
    return;
  }
  const iW = img.naturalWidth  || img.width  || w;
  const iH = img.naturalHeight || img.height || h;
  const imgRatio  = iW / iH;
  const zoneRatio = w  / h;
  let sx = 0, sy = 0, sw = iW, sh = iH;
  if (imgRatio > zoneRatio) { sw = sh * zoneRatio; sx = (iW - sw) / 2; }
  else                       { sh = sw / zoneRatio; sy = (iH - sh) / 2; }
  ctx.save();
  ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
  ctx.restore();
}

// Draw logo maintaining aspect ratio anchored at (x, y) with max bounds
function drawLogo(ctx, logoImg, x, y, maxW, maxH, alpha = 1) {
  if (!logoImg) return;
  const lW    = logoImg.naturalWidth  || logoImg.width  || maxW;
  const lH    = logoImg.naturalHeight || logoImg.height || maxH;
  const scale = Math.min(maxW / lW, maxH / lH, 1);
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.drawImage(logoImg, x, y, lW * scale, lH * scale);
  ctx.restore();
}

// Draw a CTA pill button; returns rendered button height
function drawCtaButton(ctx, text, x, y, bgColour, textColour, font, fontSize) {
  if (!text) return 0;
  ctx.save();
  ctx.font = `700 ${fontSize}px ${font}, sans-serif`;
  const label = `${text}  →`;
  const tw    = ctx.measureText(label).width;
  const pH    = Math.round(fontSize * 0.55);
  const pW    = Math.round(fontSize * 1.1);
  const bW    = tw + pW * 2;
  const bH    = fontSize + pH * 2;

  // Soft shadow on the button
  ctx.shadowColor   = rgba(bgColour, 0.40);
  ctx.shadowBlur    = Math.round(fontSize * 0.8);
  ctx.shadowOffsetY = Math.round(fontSize * 0.18);

  ctx.fillStyle = bgColour;
  roundedRect(ctx, x, y, bW, bH, bH / 2);
  ctx.fill();

  // Text — no shadow
  ctx.shadowBlur    = 0;
  ctx.shadowColor   = 'transparent';
  ctx.shadowOffsetY = 0;
  ctx.fillStyle     = textColour;
  ctx.textBaseline  = 'middle';
  ctx.fillText(label, x + pW, y + bH / 2);
  ctx.restore();
  return bH;
}

// Draw a thin accent line
function drawAccentLine(ctx, x, y, w, colour, thickness) {
  ctx.save();
  ctx.shadowBlur = 0; ctx.shadowColor = 'transparent';
  ctx.fillStyle  = colour;
  ctx.fillRect(x, y, w, thickness);
  ctx.restore();
}

// Load an Image from a src URL; resolves null on error (template still renders)
function loadImage(src) {
  return new Promise(resolve => {
    const img = new Image();
    img.onload  = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

// ─── Placeholder Background (used for thumbnails) ─────────────────────────────

function makePlaceholderCanvas(w, h) {
  const c   = document.createElement('canvas');
  c.width   = w; c.height = h;
  const ctx = c.getContext('2d');
  const g   = ctx.createLinearGradient(0, 0, w, h);
  g.addColorStop(0,   '#CBD5E1');
  g.addColorStop(0.5, '#94A3B8');
  g.addColorStop(1,   '#64748B');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
  // Subtle stripe texture
  ctx.fillStyle = 'rgba(255,255,255,0.07)';
  for (let i = 0; i < w; i += 36) ctx.fillRect(i, 0, 18, h);
  return c;
}

// ─── Template Functions ───────────────────────────────────────────────────────
// Each receives: (ctx, W, H, img, logoImg, { headline, subtext, ctaText, brandColours, fontStyle })

// Template 1: Bottom Band
// Photo fills canvas; brand-colour gradient band anchors the bottom 40%
function tBottomBand(ctx, W, H, img, logoImg, { headline, subtext, ctaText, brandColours, fontStyle }) {
  const { primary, accent } = brandColours;
  const font  = getFont(fontStyle);
  const PAD   = Math.round(W * 0.07);
  const BAND_H = Math.round(H * 0.41);
  const BAND_Y = H - BAND_H;

  // 1. Full-bleed photo
  drawCoverImage(ctx, img, 0, 0, W, H);

  // 2. Brand gradient band
  const grad = ctx.createLinearGradient(0, BAND_Y - Math.round(H * 0.08), 0, H);
  grad.addColorStop(0,   'rgba(0,0,0,0)');
  grad.addColorStop(0.28, rgba(primary, 0.80));
  grad.addColorStop(1,    rgba(primary, 0.97));
  ctx.fillStyle = grad;
  ctx.fillRect(0, BAND_Y - Math.round(H * 0.08), W, BAND_H + Math.round(H * 0.08));

  // 3. Logo — top-right with subtle pill background
  if (logoImg) {
    const logoMaxW = Math.round(W * 0.14);
    const logoMaxH = Math.round(H * 0.072);
    const lPad     = Math.round(W * 0.038);
    ctx.save();
    ctx.fillStyle = 'rgba(255,255,255,0.13)';
    roundedRect(ctx, W - logoMaxW - lPad * 1.8, lPad * 0.55, logoMaxW + lPad, logoMaxH + lPad * 0.8, Math.round(W * 0.022));
    ctx.fill();
    ctx.restore();
    drawLogo(ctx, logoImg, W - logoMaxW - lPad * 1.3, lPad, logoMaxW, logoMaxH, 0.95);
  }

  // 4. Text inside band
  const onBand = textOn(primary);
  let y = BAND_Y + Math.round(BAND_H * 0.14);
  ctx.textBaseline = 'top';

  // Accent line
  drawAccentLine(ctx, PAD, y, Math.round(W * 0.09), accent || onBand, Math.round(W * 0.006));
  y += Math.round(W * 0.021);

  // Headline
  if (headline) {
    const hSize = Math.round(W * (headline.length > 32 ? 0.050 : 0.062));
    ctx.font      = `900 ${hSize}px ${font}, sans-serif`;
    ctx.fillStyle = onBand;
    ctx.shadowBlur = 0; ctx.shadowColor = 'transparent';
    for (const line of wrapText(ctx, headline.toUpperCase(), W - PAD * 2).slice(0, 3)) {
      ctx.fillText(line, PAD, y); y += hSize * 1.18;
    }
    y += Math.round(hSize * 0.24);
  }

  // Subtext
  if (subtext) {
    const sSize = Math.round(W * 0.026);
    ctx.font      = `400 ${sSize}px ${font}, sans-serif`;
    ctx.fillStyle = rgba(onBand === '#FFFFFF' ? '#FFFFFF' : '#111827', 0.80);
    for (const line of wrapText(ctx, subtext, W - PAD * 2).slice(0, 2)) {
      ctx.fillText(line, PAD, y); y += sSize * 1.52;
    }
    y += Math.round(sSize * 0.55);
  }

  // CTA
  if (ctaText && y < H - Math.round(H * 0.055)) {
    const ctaSize = Math.round(W * 0.024);
    const ctaBg   = isLight(primary) ? '#111827' : (accent || '#FFFFFF');
    drawCtaButton(ctx, ctaText, PAD, y, ctaBg, textOn(ctaBg), font, ctaSize);
  }
}

// Template 2: Split Panel
// Solid brand colour panel left 42% · photo right 58%
function tSplitPanel(ctx, W, H, img, logoImg, { headline, subtext, ctaText, brandColours, fontStyle }) {
  const { primary, secondary, accent } = brandColours;
  const font  = getFont(fontStyle);
  const SPLIT = Math.round(W * 0.42);
  const PAD   = Math.round(W * 0.054);

  // 1. Brand primary left panel
  ctx.fillStyle = primary;
  ctx.fillRect(0, 0, SPLIT, H);

  // 2. Photo right panel
  drawCoverImage(ctx, img, SPLIT, 0, W - SPLIT, H);

  // 3. Accent separator strip
  const stripW = Math.round(W * 0.008);
  ctx.fillStyle = accent || secondary || rgba('#FFFFFF', 0.6);
  ctx.fillRect(SPLIT - Math.round(stripW / 2), 0, stripW, H);

  // 4. Logo top of left panel
  if (logoImg) {
    const logoMaxW = Math.round(SPLIT * 0.65);
    const logoMaxH = Math.round(H * 0.095);
    drawLogo(ctx, logoImg, PAD, Math.round(H * 0.06), logoMaxW, logoMaxH, 0.95);
  }

  // 5. Text — vertically centred in left panel
  const onPrimary = textOn(primary);
  let y = Math.round(H * (logoImg ? 0.26 : 0.18));
  ctx.textBaseline = 'top';
  ctx.shadowBlur   = 0; ctx.shadowColor = 'transparent';

  drawAccentLine(ctx, PAD, y, Math.round(SPLIT * 0.22), accent || onPrimary, Math.round(W * 0.005));
  y += Math.round(H * 0.026);

  if (headline) {
    const hSize = Math.round(W * (headline.length > 26 ? 0.044 : 0.054));
    ctx.font      = `800 ${hSize}px ${font}, sans-serif`;
    ctx.fillStyle = onPrimary;
    for (const line of wrapText(ctx, headline, SPLIT - PAD * 2).slice(0, 4)) {
      ctx.fillText(line, PAD, y); y += hSize * 1.2;
    }
    y += Math.round(hSize * 0.28);
  }

  if (subtext) {
    const sSize = Math.round(W * 0.023);
    ctx.font      = `400 ${sSize}px ${font}, sans-serif`;
    ctx.fillStyle = rgba(onPrimary === '#FFFFFF' ? '#FFFFFF' : '#111827', 0.75);
    for (const line of wrapText(ctx, subtext, SPLIT - PAD * 2).slice(0, 3)) {
      ctx.fillText(line, PAD, y); y += sSize * 1.52;
    }
    y += Math.round(sSize * 0.8);
  }

  if (ctaText) {
    const ctaSize = Math.round(W * 0.022);
    const ctaBg   = accent || (isLight(primary) ? '#111827' : '#FFFFFF');
    drawCtaButton(ctx, ctaText, PAD, y, ctaBg, textOn(ctaBg), font, ctaSize);
  }
}

// Template 3: Frosted Card
// Full-bleed photo · floating frosted glass card at bottom centre
function tFrostedCard(ctx, W, H, img, logoImg, { headline, subtext, ctaText, brandColours, fontStyle }) {
  const { primary, accent } = brandColours;
  const font = getFont(fontStyle);
  const PAD  = Math.round(W * 0.06);

  // 1. Full-bleed photo
  drawCoverImage(ctx, img, 0, 0, W, H);

  // 2. Vignette
  const vig = ctx.createRadialGradient(W / 2, H * 0.4, H * 0.1, W / 2, H * 0.5, H * 0.75);
  vig.addColorStop(0, 'rgba(0,0,0,0)');
  vig.addColorStop(1, 'rgba(0,0,0,0.48)');
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, W, H);

  // 3. Frosted glass card
  const CARD_W = Math.round(W * 0.84);
  const CARD_H = Math.round(H * 0.43);
  const CARD_X = Math.round((W - CARD_W) / 2);
  const CARD_Y = Math.round(H * 0.51);
  const CARD_R = Math.round(W * 0.034);

  ctx.save();
  roundedRect(ctx, CARD_X, CARD_Y, CARD_W, CARD_H, CARD_R);
  ctx.clip();
  // Brand tint layer
  ctx.fillStyle = rgba(primary, 0.20);
  ctx.fillRect(CARD_X, CARD_Y, CARD_W, CARD_H);
  // Frosted white shimmer
  ctx.fillStyle = 'rgba(255,255,255,0.11)';
  ctx.fillRect(CARD_X, CARD_Y, CARD_W, CARD_H);
  ctx.restore();

  // Card border
  ctx.save();
  roundedRect(ctx, CARD_X, CARD_Y, CARD_W, CARD_H, CARD_R);
  ctx.strokeStyle = 'rgba(255,255,255,0.30)';
  ctx.lineWidth   = Math.round(W * 0.003);
  ctx.stroke();
  ctx.restore();

  // 4. Logo top-right (on photo)
  if (logoImg) {
    const logoMaxW = Math.round(W * 0.14);
    const logoMaxH = Math.round(H * 0.072);
    drawLogo(ctx, logoImg, W - logoMaxW - PAD, PAD, logoMaxW, logoMaxH, 0.95);
  }

  // 5. Text inside card
  const IP  = Math.round(CARD_W * 0.07); // inner padding
  let y = CARD_Y + Math.round(CARD_H * 0.15);
  ctx.textBaseline = 'top';

  drawAccentLine(ctx, CARD_X + IP, y, Math.round(CARD_W * 0.09), accent || '#FFFFFF', Math.round(W * 0.005));
  y += Math.round(H * 0.022);

  if (headline) {
    const hSize = Math.round(CARD_W * (headline.length > 28 ? 0.054 : 0.066));
    ctx.font      = `800 ${hSize}px ${font}, sans-serif`;
    ctx.fillStyle = '#FFFFFF';
    ctx.shadowColor = 'rgba(0,0,0,0.55)'; ctx.shadowBlur = 12;
    for (const line of wrapText(ctx, headline.toUpperCase(), CARD_W - IP * 2).slice(0, 2)) {
      ctx.fillText(line, CARD_X + IP, y); y += hSize * 1.18;
    }
    ctx.shadowBlur = 0; ctx.shadowColor = 'transparent';
    y += Math.round(hSize * 0.20);
  }

  if (subtext) {
    const sSize = Math.round(CARD_W * 0.033);
    ctx.font      = `400 ${sSize}px ${font}, sans-serif`;
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    for (const line of wrapText(ctx, subtext, CARD_W - IP * 2).slice(0, 2)) {
      ctx.fillText(line, CARD_X + IP, y); y += sSize * 1.52;
    }
    y += Math.round(sSize * 0.5);
  }

  if (ctaText && y < CARD_Y + CARD_H - Math.round(CARD_H * 0.14)) {
    const ctaSize = Math.round(CARD_W * 0.031);
    drawCtaButton(ctx, ctaText, CARD_X + IP, y, accent || primary, textOn(accent || primary), font, ctaSize);
  }
}

// Template 4: Bold Overlay
// Brand-tinted overlay covers photo · centred headline + CTA
function tBoldOverlay(ctx, W, H, img, logoImg, { headline, subtext, ctaText, brandColours, fontStyle }) {
  const { primary, accent } = brandColours;
  const font = getFont(fontStyle);
  const PAD  = Math.round(W * 0.08);

  // 1. Full-bleed photo
  drawCoverImage(ctx, img, 0, 0, W, H);

  // 2. Brand-tinted overlay
  ctx.fillStyle = rgba(primary, 0.73);
  ctx.fillRect(0, 0, W, H);

  // 3. Subtle diagonal texture lines
  ctx.save();
  ctx.strokeStyle = rgba(isLight(primary) ? '#000000' : '#FFFFFF', 0.04);
  ctx.lineWidth   = Math.round(W * 0.006);
  for (let i = -H; i < W + H; i += Math.round(W * 0.09)) {
    ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i + H, H); ctx.stroke();
  }
  ctx.restore();

  // 4. Logo centred at top
  if (logoImg) {
    const logoMaxW = Math.round(W * 0.22);
    const logoMaxH = Math.round(H * 0.105);
    const lW = logoImg.naturalWidth  || logoImg.width  || logoMaxW;
    const lH = logoImg.naturalHeight || logoImg.height || logoMaxH;
    const sc = Math.min(logoMaxW / lW, logoMaxH / lH);
    ctx.save();
    ctx.globalAlpha = 0.95;
    ctx.drawImage(logoImg, (W - lW * sc) / 2, Math.round(H * 0.07), lW * sc, lH * sc);
    ctx.restore();
  }

  // 5. Centred text
  const onPrimary = textOn(primary);
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'top';
  ctx.shadowBlur   = 0; ctx.shadowColor = 'transparent';
  let y = Math.round(H * (logoImg ? 0.30 : 0.22));

  // Centred accent line
  const lineW = Math.round(W * 0.12);
  drawAccentLine(ctx, (W - lineW) / 2, y, lineW, accent || onPrimary, Math.round(W * 0.007));
  y += Math.round(H * 0.032);

  if (headline) {
    const hSize = Math.round(W * (headline.length > 25 ? 0.055 : 0.070));
    ctx.font      = `900 ${hSize}px ${font}, sans-serif`;
    ctx.fillStyle = onPrimary;
    for (const line of wrapText(ctx, headline.toUpperCase(), W - PAD * 2).slice(0, 3)) {
      ctx.fillText(line, W / 2, y); y += hSize * 1.15;
    }
    y += Math.round(hSize * 0.28);
  }

  if (subtext) {
    const sSize = Math.round(W * 0.028);
    ctx.font      = `400 ${sSize}px ${font}, sans-serif`;
    ctx.fillStyle = rgba(onPrimary === '#FFFFFF' ? '#FFFFFF' : '#111827', 0.82);
    for (const line of wrapText(ctx, subtext, W - PAD * 2.5).slice(0, 2)) {
      ctx.fillText(line, W / 2, y); y += sSize * 1.55;
    }
    y += Math.round(sSize * 0.7);
  }

  // Centred CTA
  if (ctaText) {
    const ctaSize = Math.round(W * 0.026);
    ctx.font = `700 ${ctaSize}px ${font}, sans-serif`;
    const label = `${ctaText}  →`;
    const tw    = ctx.measureText(label).width;
    const pH    = Math.round(ctaSize * 0.55);
    const pW    = Math.round(ctaSize * 1.1);
    const bW    = tw + pW * 2;
    const bH    = ctaSize + pH * 2;
    const bX    = (W - bW) / 2;
    ctx.save();
    ctx.shadowColor   = rgba(accent || primary, 0.40);
    ctx.shadowBlur    = Math.round(ctaSize * 0.8);
    ctx.shadowOffsetY = Math.round(ctaSize * 0.18);
    ctx.fillStyle = accent || onPrimary;
    roundedRect(ctx, bX, y, bW, bH, bH / 2);
    ctx.fill();
    ctx.shadowBlur = 0; ctx.shadowColor = 'transparent'; ctx.shadowOffsetY = 0;
    ctx.fillStyle    = textOn(accent || primary);
    ctx.textBaseline = 'middle';
    ctx.fillText(label, W / 2, y + bH / 2);
    ctx.restore();
  }

  ctx.textAlign = 'left';
}

// Template 5: Top Bar (Brand Bar)
// Solid brand header · photo middle · dark gradient footer with text
function tTopBar(ctx, W, H, img, logoImg, { headline, subtext, ctaText, brandColours, fontStyle }) {
  const { primary, secondary, accent } = brandColours;
  const font   = getFont(fontStyle);
  const PAD    = Math.round(W * 0.065);
  const BAR_H  = Math.round(H * 0.18);
  const FOOT_H = Math.round(H * 0.28);
  const PHOTO_Y = BAR_H;
  const PHOTO_H = H - BAR_H - FOOT_H;
  const FOOT_Y  = PHOTO_Y + PHOTO_H;

  // 1. Photo in middle
  drawCoverImage(ctx, img, 0, PHOTO_Y, W, PHOTO_H);

  // 2. Brand top bar
  ctx.fillStyle = primary;
  ctx.fillRect(0, 0, W, BAR_H);

  // Accent strip at bar bottom
  ctx.fillStyle = accent || secondary || 'rgba(255,255,255,0.25)';
  ctx.fillRect(0, BAR_H - Math.round(W * 0.005), W, Math.round(W * 0.005));

  // 3. Logo inside top bar
  const onPrimary = textOn(primary);
  if (logoImg) {
    const logoMaxW = Math.round(W * 0.26);
    const logoMaxH = Math.round(BAR_H * 0.58);
    drawLogo(ctx, logoImg, PAD, Math.round((BAR_H - logoMaxH) / 2), logoMaxW, logoMaxH, 0.95);
  } else {
    const nSize = Math.round(BAR_H * 0.34);
    ctx.font      = `700 ${nSize}px ${font}, sans-serif`;
    ctx.fillStyle = onPrimary;
    ctx.textBaseline = 'middle';
    ctx.fillText('YOUR BRAND', PAD, BAR_H / 2);
    ctx.textBaseline = 'alphabetic';
  }

  // 4. Dark footer
  const footGrad = ctx.createLinearGradient(0, FOOT_Y, 0, H);
  footGrad.addColorStop(0, 'rgba(0,0,0,0.82)');
  footGrad.addColorStop(1, 'rgba(0,0,0,0.95)');
  ctx.fillStyle = footGrad;
  ctx.fillRect(0, FOOT_Y, W, FOOT_H);

  // Accent line at top of footer
  drawAccentLine(ctx, PAD, FOOT_Y + Math.round(H * 0.012), Math.round(W * 0.10), accent || primary, Math.round(W * 0.005));

  // 5. Text in footer
  let y = FOOT_Y + Math.round(FOOT_H * 0.20);
  ctx.textBaseline = 'top';
  ctx.shadowBlur   = 0; ctx.shadowColor = 'transparent';

  if (headline) {
    const hSize = Math.round(W * (headline.length > 28 ? 0.047 : 0.057));
    ctx.font      = `800 ${hSize}px ${font}, sans-serif`;
    ctx.fillStyle = '#FFFFFF';
    for (const line of wrapText(ctx, headline.toUpperCase(), W - PAD * 2).slice(0, 2)) {
      ctx.fillText(line, PAD, y); y += hSize * 1.18;
    }
    y += Math.round(hSize * 0.22);
  }

  if (subtext) {
    const sSize = Math.round(W * 0.024);
    ctx.font      = `400 ${sSize}px ${font}, sans-serif`;
    ctx.fillStyle = 'rgba(255,255,255,0.78)';
    for (const line of wrapText(ctx, subtext, W - PAD * 2).slice(0, 2)) {
      ctx.fillText(line, PAD, y); y += sSize * 1.52;
    }
    y += Math.round(sSize * 0.6);
  }

  if (ctaText && y < FOOT_Y + FOOT_H - Math.round(FOOT_H * 0.14)) {
    const ctaSize = Math.round(W * 0.022);
    drawCtaButton(ctx, ctaText, PAD, y, accent || primary, textOn(accent || primary), font, ctaSize);
  }
}

// Template 6: Editorial
// Photo hero occupies top 58% · clean white panel below
function tEditorial(ctx, W, H, img, logoImg, { headline, subtext, ctaText, brandColours, fontStyle }) {
  const { primary, accent } = brandColours;
  const font    = getFont(fontStyle);
  const PAD     = Math.round(W * 0.07);
  const PHOTO_H = Math.round(H * 0.58);
  const PANEL_Y = PHOTO_H;
  const PANEL_H = H - PHOTO_H;

  // 1. Photo top
  drawCoverImage(ctx, img, 0, 0, W, PHOTO_H);

  // Gentle blend at photo bottom edge
  const blendGrad = ctx.createLinearGradient(0, PHOTO_H - Math.round(H * 0.07), 0, PHOTO_H);
  blendGrad.addColorStop(0, 'rgba(0,0,0,0)');
  blendGrad.addColorStop(1, 'rgba(0,0,0,0.10)');
  ctx.fillStyle = blendGrad;
  ctx.fillRect(0, PHOTO_H - Math.round(H * 0.07), W, Math.round(H * 0.07));

  // 2. White panel
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, PANEL_Y, W, PANEL_H);

  // 3. Brand accent line at panel top
  drawAccentLine(ctx, PAD, PANEL_Y + Math.round(H * 0.018), Math.round(W * 0.12), accent || primary, Math.round(W * 0.007));

  // 4. Logo on photo (top-right, white pill background)
  if (logoImg) {
    const logoMaxW = Math.round(W * 0.15);
    const logoMaxH = Math.round(PHOTO_H * 0.11);
    const lPad     = Math.round(W * 0.022);
    ctx.save();
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    roundedRect(ctx, W - logoMaxW - lPad * 3, lPad, logoMaxW + lPad * 2, logoMaxH + lPad, Math.round(W * 0.018));
    ctx.fill();
    ctx.restore();
    drawLogo(ctx, logoImg, W - logoMaxW - lPad * 2, Math.round(lPad * 1.3), logoMaxW, logoMaxH, 0.95);
  }

  // 5. Text in white panel
  let y = PANEL_Y + Math.round(PANEL_H * 0.20);
  ctx.textBaseline = 'top';
  ctx.shadowBlur   = 0; ctx.shadowColor = 'transparent';

  if (headline) {
    const hSize = Math.round(W * (headline.length > 30 ? 0.046 : 0.056));
    ctx.font      = `700 ${hSize}px ${font}, sans-serif`;
    ctx.fillStyle = '#111827';
    for (const line of wrapText(ctx, headline, W - PAD * 2).slice(0, 2)) {
      ctx.fillText(line, PAD, y); y += hSize * 1.22;
    }
    y += Math.round(hSize * 0.22);
  }

  if (subtext) {
    const sSize = Math.round(W * 0.024);
    ctx.font      = `400 ${sSize}px ${font}, sans-serif`;
    ctx.fillStyle = '#6B7280';
    for (const line of wrapText(ctx, subtext, W - PAD * 2).slice(0, 2)) {
      ctx.fillText(line, PAD, y); y += sSize * 1.52;
    }
    y += Math.round(sSize * 0.6);
  }

  if (ctaText && y < PANEL_Y + PANEL_H - Math.round(PANEL_H * 0.14)) {
    const ctaSize = Math.round(W * 0.022);
    drawCtaButton(ctx, ctaText, PAD, y, primary, textOn(primary), font, ctaSize);
  }
}

// ─── Template Dispatch ────────────────────────────────────────────────────────

const TEMPLATE_FNS = {
  bottom_band:  tBottomBand,
  split_panel:  tSplitPanel,
  frosted_card: tFrostedCard,
  bold_overlay: tBoldOverlay,
  top_bar:      tTopBar,
  editorial:    tEditorial,
};

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Render a template to a data URL.
 * @param {string}  templateId   – one of TEMPLATES[*].id
 * @param {object}  options      – { bgDataUrl, logoDataUrl, headline, subtext, ctaText, aspectRatio, brandColours, fontStyle }
 * @param {number[]} [overrideDims] – [W, H] — override canvas dimensions (for thumbnails)
 * @returns {Promise<string>}    – PNG data URL
 */
export async function renderTemplate(templateId, options = {}, overrideDims = null) {
  const {
    bgDataUrl,
    logoDataUrl,
    headline    = '',
    subtext     = '',
    ctaText     = '',
    aspectRatio = 'square',
    brandColours = {},
    fontStyle    = 'modern_clean',
  } = options;

  await ensureFontsLoaded();

  // Load images in parallel
  const [img, logoImg] = await Promise.all([
    bgDataUrl    ? loadImage(bgDataUrl)    : Promise.resolve(null),
    logoDataUrl  ? loadImage(logoDataUrl)  : Promise.resolve(null),
  ]);

  const [W, H] = overrideDims || CANVAS_DIMS[aspectRatio] || CANVAS_DIMS.square;
  const canvas  = document.createElement('canvas');
  canvas.width  = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Normalise brand colours with sensible defaults
  const colours = {
    primary:   brandColours.primary   || '#6366F1',
    secondary: brandColours.secondary || '#4F46E5',
    accent:    brandColours.accent    || '#F59E0B',
  };

  const fn = TEMPLATE_FNS[templateId] || TEMPLATE_FNS.bottom_band;
  fn(ctx, W, H, img, logoImg, {
    headline,
    subtext,
    ctaText,
    brandColours: colours,
    fontStyle,
  });

  return canvas.toDataURL('image/png', 1.0);
}

/**
 * Render a small thumbnail of a template using a grey gradient placeholder.
 * @param {string}  templateId
 * @param {object}  brandColours  – { primary, secondary, accent }
 * @param {string}  fontStyle
 * @param {number}  [size=320]    – pixel size (square)
 * @returns {Promise<string>}     – PNG data URL
 */
export async function renderThumbnail(templateId, brandColours = {}, fontStyle = 'modern_clean', size = 320) {
  const placeholderCanvas = makePlaceholderCanvas(size, size);
  const bgDataUrl         = placeholderCanvas.toDataURL('image/png');

  return renderTemplate(
    templateId,
    {
      bgDataUrl,
      logoDataUrl: null,
      headline:    'Your Headline',
      subtext:     'Supporting text for your post',
      ctaText:     'Book Now',
      aspectRatio: 'square',
      brandColours,
      fontStyle,
    },
    [size, size]
  );
}
